import { LGraphNode, LiteGraph } from "litegraph.js";
import { GlobalConfig } from "../GlobalConfig.js";
import { animateGridPattern } from "../effects/GridAnimatorEffects.js";

const RATIOS = {
  "Square (1:1)":      { w: 1, h: 1 },
  "IG Portrait (4:5)": { w: 4, h: 5 },
  "IG Story (9:16)":   { w: 9, h: 16 },
  "Landscape (16:9)":  { w: 16, h: 9 },
  "A4 Portrait":       { w: 210, h: 297 },
  "A4 Landscape":      { w: 297, h: 210 },
};

const BASE_WIDTH = 1080; // Instagram-standard width

export class GridAnimatorNode extends LGraphNode {
    constructor() {
        super();
        this.title = "Grid Animator";

        this.properties = {
            ratio: "Square (1:1)",
            canvasWidth: BASE_WIDTH,
            canvasHeight: BASE_WIDTH,
            bgColor: "#f6f6f2",
            startTime: 0,
            hasStarted: false,
        };

        this.addInput("TextCanvas", "canvas");
        this.addInput("Pattern", "array");
        this.addInput("Time", "number");
        this.addInput("Color", "string");
        this.addOutput("Canvas", "canvas");

        this.canvas = document.createElement("canvas");
        this.ctx = this.canvas.getContext("2d");
        this._reframedCanvas = document.createElement("canvas");
        this._reframedCtx = this._reframedCanvas.getContext("2d");
        this.brushColor = GlobalConfig.brushColor;

        // ─── Widgets ──────────────────────────────────
        this.addWidget("combo", "Ratio", this.properties.ratio, (val) => {
            this.properties.ratio = val;
            this._applyRatio();
        }, { values: Object.keys(RATIOS) });

        this.addWidget("combo", "Background", this.properties.bgColor, (val) => {
            this.properties.bgColor = val;
        }, { values: ["#f6f6f2", "#ffffff", "#000000", "#222222", "transparent"] });

        // ─── Node appearance ──────────────────────────
        this.size = [300, 400];
        this.resizable = true;
        this.mode = LiteGraph.ALWAYS;
        this.color = "#2a2a2a";
        this.bgcolor = "#333333";

        this._applyRatio();

        GlobalConfig.events.addEventListener("brushColor", (event) => {
            this.brushColor = event.detail;
        });
    }

    _applyRatio() {
        const r = RATIOS[this.properties.ratio];
        if (!r) return;

        // Always 1080 wide, height derived from ratio
        const w = BASE_WIDTH;
        const h = Math.round(BASE_WIDTH * (r.h / r.w));

        this.properties.canvasWidth = w;
        this.properties.canvasHeight = h;

        this._resizeNodeToRatio(w, h);
    }

    // Returns the Y position where the preview area starts (below slots + widgets)
    _getPreviewTop() {
        // Title ~26px, 4 input slots × 20px = 80px, 2 widgets × 30px = 60px, gap 6px
        return 172;
    }

    _resizeNodeToRatio(cw, ch) {
        const pad = 6;
        const infoHeight = 20;
        const previewTop = this._getPreviewTop();
        const availW = this.size[0] - pad * 2;
        const previewH = Math.round(availW * (ch / cw));
        // Clamp so tall ratios don't make the node huge
        const clampedH = Math.min(400, previewH);
        this.size[1] = previewTop + clampedH + infoHeight + pad;
    }

    onDrawForeground(ctx) {
        if (!this.canvas || this.canvas.width === 0) return;

        const pad = 6;
        const infoHeight = 20;
        const previewTop = this._getPreviewTop();

        // Fill full node width — no letterboxing
        const dx = pad;
        const dy = previewTop;
        const dw = this.size[0] - pad * 2;
        const dh = this.size[1] - previewTop - infoHeight - pad;

        if (dh < 20 || dw < 20) return;

        // Background color
        const bg = this.properties.bgColor;
        if (bg !== "transparent") {
            ctx.fillStyle = bg;
            ctx.fillRect(dx, dy, dw, dh);
        } else {
            ctx.save();
            ctx.beginPath();
            ctx.rect(dx, dy, dw, dh);
            ctx.clip();
            const sq = 8;
            for (let y = 0; y < dh; y += sq) {
                for (let x = 0; x < dw; x += sq) {
                    ctx.fillStyle = ((Math.floor(x / sq) + Math.floor(y / sq)) % 2 === 0) ? "#ccc" : "#fff";
                    ctx.fillRect(dx + x, dy + y,
                        Math.min(sq, dw - x), Math.min(sq, dh - y));
                }
            }
            ctx.restore();
        }

        // Draw animation canvas — fills the preview area
        ctx.save();
        ctx.beginPath();
        ctx.rect(dx, dy, dw, dh);
        ctx.clip();
        ctx.drawImage(this.canvas, dx, dy, dw, dh);
        ctx.restore();

        // Subtle border
        ctx.strokeStyle = "rgba(255,255,255,0.1)";
        ctx.lineWidth = 1;
        ctx.strokeRect(dx, dy, dw, dh);

        // Info bar
        const infoY = this.size[1] - infoHeight;
        ctx.fillStyle = "rgba(255,255,255,0.35)";
        ctx.font = "9px -apple-system, sans-serif";
        ctx.fillText(
            `${this.properties.canvasWidth} × ${this.properties.canvasHeight}px  ·  ${this.properties.ratio}`,
            pad, infoY + 12
        );
    }

    onExecute() {
        const textCanvas = this.getInputData(0);
        const pattern = this.getInputData(1);
        let timeValue = this.getInputData(2);
        const colorInput = this.getInputData(3);

        const color = colorInput || this.brushColor;
        let fraction = timeValue != null ? timeValue : this.computeAutoPlayFraction();

        if (!pattern || !textCanvas) {
            this.setOutputData(0, this.canvas);
            return;
        }

        const outW = this.properties.canvasWidth;
        const outH = this.properties.canvasHeight;
        const rc = this._reframedCanvas;
        if (rc.width !== outW || rc.height !== outH) {
            rc.width = outW;
            rc.height = outH;
        }
        this._reframedCtx.clearRect(0, 0, outW, outH);
        const bounce = textCanvas.bounceOffset || { x: 0, y: 0 };
        const dx = (outW - textCanvas.width) / 2 + bounce.x;
        const dy = (outH - textCanvas.height) / 2 + bounce.y;
        this._reframedCtx.drawImage(textCanvas, dx, dy);

        // Pass text bounds (adjusted for centering + bounce)
        let textBounds = null;
        if (textCanvas.textBounds) {
            const tb = textCanvas.textBounds;
            textBounds = { x: tb.x + dx, y: tb.y + dy, w: tb.w, h: tb.h };
        }

        this.canvas = animateGridPattern(
            pattern, pattern.length, rc, fraction, color,
            outW, outH, 'main', textBounds
        );

        this.setOutputData(0, this.canvas);
        this.setDirtyCanvas(true);
    }

    computeAutoPlayFraction() {
        if (!this.properties.hasStarted) {
            this.properties.startTime = performance.now();
            this.properties.hasStarted = true;
        }
        const elapsed = performance.now() - this.properties.startTime;
        return Math.min(1, elapsed / 5000);
    }

    onResize() {
        this.size[0] = Math.max(200, this.size[0]);
        this._resizeNodeToRatio(this.properties.canvasWidth, this.properties.canvasHeight);
    }
}

LiteGraph.registerNodeType("display/GridAnimatorNode", GridAnimatorNode);
